// Omfattningsläge: hela byggprocessen eller enbart projektering.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchScopeMode, setScopeMode } from "@/lib/basProcess/simpleMode";
import { Compass } from "lucide-react";

interface Props {
  projectId: string;
  canEdit?: boolean;
}

const ProjectScopeMode: React.FC<Props> = ({ projectId, canEdit = true }) => {
  const [mode, setMode] = useState("full");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchScopeMode(projectId).then(setMode).catch(() => undefined);
  }, [projectId]);

  const change = async (value: string) => {
    setMode(value);
    setSaving(true);
    try {
      await setScopeMode(projectId, value);
      toast.success(
        value === "design_only"
          ? "Projektet är satt till enbart projektering"
          : "Projektet omfattar hela byggprocessen"
      );
    } catch {
      toast.error("Kunde inte spara omfattningen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Compass className="h-4 w-4 text-primary" />
          Projektets omfattning
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Ett rent projekteringsuppdrag slutar vid överlämnade handlingar. Då markeras
          utförande- och avslutsdokumenten som ej aktuella och färdigställandegraden räknas
          bara på det som faktiskt ingår.
        </p>
      </CardHeader>
      <CardContent className="max-w-sm">
        <Label>Läge</Label>
        <Select value={mode} onValueChange={change} disabled={!canEdit || saving}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Hela byggprocessen</SelectItem>
            <SelectItem value="design_only">Enbart projektering</SelectItem>
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
};

export default ProjectScopeMode;
